/*
 * Copyright (c) 2022. Revo Digital
 * ---
 * Author: gabriele
 * File: PreviewDialog.tsx
 * Project: mle-client-n
 * Committed last: 2022/5/27 @ 1723
 * ---
 * Description:
 */

import {observer} from 'mobx-react';
import classNames from 'classnames';
import {currentThemeIsDark} from '../../../common/utils';
import {Button, Callout, Classes, Dialog, Icon, Intent, NonIdealState, Spinner} from '@blueprintjs/core';
import {BaseDialogProps, HasEditor} from '../../../common/props';
import React, {useEffect, useState} from 'react';
import Pamela from '@revodigital/pamela';
import {MpesInstanceManager} from '@revodigital/mpes';
import {ActiveRevisionSelect} from '../select/data-origin/ActiveRevisionSelect';
import {MyLabelLoginDialog} from "./MyLabelLoginDialog";

interface Props extends HasEditor, BaseDialogProps {
    onExport: () => void;
    onConnectRevisions: () => void;
}

const DIV_ID = 'stage-container';

export const PreviewDialog = observer((props: Props) => {
    const [isLoading, setIsLoading] = useState(false);
    const [errored, setErrored] = useState(false);
    const [selectedRevision, setSelectedRevision] = useState<number>();
    const [showLogin, setShowLogin] = useState(false);

    useEffect(() => {
        (async () => {
            setIsLoading(true);

            try {
                // Remove previous stage
                const element = document.getElementById(DIV_ID);
                if (element) element.innerHTML = '';

                const res = await props.editor.exportFeature.prepareForExport(new Map(),
                    selectedRevision);
                const labelSize = props.editor.labelSize;

                const stage = new Pamela.Stage({
                    container: DIV_ID,
                    width: labelSize.getWidth(),
                    height: labelSize.getHeight(),
                    listening: false,
                    draggable: false,
                });

                const decoded = await MpesInstanceManager.getDecoderInstance().decodeBuffer(
                    res);
                decoded.content.data.forEach(it => stage.add(it));

                stage.draw();
                setErrored(false);
            } catch (e) {
                console.error(e);
                setErrored(true);
            }

            setIsLoading(false);
        })();
    }, [selectedRevision]);

    const activeRevision = (
        <div>
            <div className="my-4 bp4-text-muted mle-info-heading">
                Revisione attiva
            </div>
            <div className="my-2 bp4-text-muted">Seleziona la revisione attiva
                per l'anteprima dell'etichetta.
                Non verrà cambiata quella del progetto.
            </div>
            <ActiveRevisionSelect
                disabled={!props.editor.mylabelAuthFeature.isConnected}
                onRevisionSelect={recipe => {
                    setSelectedRevision(recipe.id);
                }}
                editor={props.editor}
            />
        </div>
    )

    /**
     * Mostrato quando non è specificata una revisione attiva
     */
    const noRevision = (
        <div className="my-4">
            <Callout intent={Intent.WARNING} title="Collega revisioni">
                <div className="flex flex-col">
                    <div>
                        Non è stata collegata nessuna revisione a questa etichetta, collegane una per sfruttare le
                        origini dati al meglio.
                        Tutte le origini dati sono state trasformate in '-'.
                    </div>
                    <Button className="w-16 my-2" onClick={() => {
                        const authFeature = props.editor.mylabelAuthFeature;

                        if (!authFeature.isConnected || !authFeature.hasCredentials)
                            setShowLogin(true);
                        else props.onConnectRevisions();
                    }}
                            intent={Intent.WARNING}>Collega</Button>
                </div>
            </Callout>
        </div>
    )

    const revisionFeature = props.editor.recipeSwitcher;

    return (
        <Dialog
            usePortal
            className={classNames({'bp4-dark': currentThemeIsDark()})}
            style={{width: '80vw', height: '70vh'}}
            isOpen={props.isOpen}
            title="Anteprima esportazione"
            onClose={props.onClose}
            canOutsideClickClose={false}
        >
            <div className={Classes.DIALOG_BODY}>
                <div className="flex flex-row">
                    <div style={{height: '60vh', width: '75vh'}}
                         className="overflow-auto">
                        <div className={classNames('p-2 pb-2 pr-2 rounded-sm', {
                            'bg-bpdisabled-dark': currentThemeIsDark(),
                            'bg-bpdisabled-light': !currentThemeIsDark()
                        })}>
                            {
                                isLoading && <Spinner size={20}/>
                            }
                            <div id={DIV_ID}>
                            </div>
                            {
                                errored && !isLoading &&
                                <div className="h-40 flex flex-row justify-center">
                                    <NonIdealState
                                        title={<div className="text-red-300">Impossibile caricare
                                            anteprima</div>}
                                        icon={<Icon icon="warning-sign" intent={Intent.DANGER}
                                                    size={40}/>}
                                        description={<div className="bp4-text-muted">Si sono
                                            verificati degli errori durante il caricamento
                                            dell'anteprima. Riprova o contatta il supporto.</div>}/>
                                </div>
                            }
                        </div>
                    </div>
                    <div className="mx-4 flex flex-col w-1/3">
                        <div>
                            <div className="bp4-text-muted mle-info-heading">
                                Proprietà etichetta
                            </div>
                            <div className={classNames('pt-2 pb-2 pr-2 rounded-sm', {
                                'bg-bpdisabled-dark': currentThemeIsDark(),
                                'bg-bpdisabled-light': !currentThemeIsDark()
                            })}>
                                <table
                                    className="bp4-html-table bp4-small bp4-html-table-bordered bp4-html-table-condensed bp4-html-table-striped bp4-interactive">
                                    <tr>
                                        <td><strong>Larghezza</strong></td>
                                        <td>{props.editor.pixelToCurrent(props.editor.labelSize.getWidth())}{props.editor.projectData.projectOptions.measureUnit}
                                        </td>
                                    </tr>
                                    <tr>
                                        <td><strong>Altezza</strong></td>
                                        <td>{props.editor.pixelToCurrent(props.editor.labelSize.getHeight())}{props.editor.projectData.projectOptions.measureUnit}
                                        </td>
                                    </tr>
                                </table>
                            </div>
                            <Button className="w-full mt-2" icon="cloud-download"
                                    intent={Intent.PRIMARY}
                                    onClick={props.onExport}>Esporta</Button>
                            <Button className="w-full mt-2" icon="data-connection" small
                                    disabled>Aggiorna origini dati</Button>
                        </div>
                        {
                            revisionFeature.hasActiveCompany ?
                                activeRevision : noRevision
                        }
                        {
                            showLogin && <MyLabelLoginDialog onLoginSuccess={() => {
                                setShowLogin(false);
                                props.onConnectRevisions();
                            }} editor={props.editor} isOpen={showLogin} onClose={() => setShowLogin(false)}/>
                        }
                    </div>
                </div>
            </div>
        </Dialog>
    );
});
